/**
 * FleetTimeline — SVG Gantt chart showing vehicle depot availability.
 *
 * 24-hour horizontal axis. One row per vehicle.
 * Gold bars = at depot (available). Dark bars = on route. Amber = flagged/missing.
 *
 * Props:
 *   vehicles: array of extracted vehicles with depot_availability
 *   onVehicleClick: (index) => void — called when a flagged vehicle is clicked
 */
import React, { useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';

const ROW_HEIGHT = 28;
const LABEL_WIDTH = 180;
const HOUR_WIDTH = 40;
const CHART_WIDTH = HOUR_WIDTH * 24;
const HEADER_HEIGHT = 24;

function timeToMinutes(timeStr) {
    if (!timeStr || timeStr === 'MISSING') return null;
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
}

function FleetTimeline({ vehicles = [], onVehicleClick }) {
    const totalHeight = HEADER_HEIGHT + vehicles.length * ROW_HEIGHT + 8;

    const hours = useMemo(() => Array.from({ length: 25 }, (_, i) => i), []);

    return (
        <Box sx={{ overflowX: 'auto', mb: 2 }}>
            <svg
                width={LABEL_WIDTH + CHART_WIDTH + 16}
                height={totalHeight}
                style={{ display: 'block', minWidth: '100%' }}
            >
                {/* Hour labels */}
                {hours.map((h) => (
                    <text
                        key={`h-${h}`}
                        x={LABEL_WIDTH + h * HOUR_WIDTH}
                        y={HEADER_HEIGHT - 4}
                        fontSize={10}
                        fill={vortxColors.stone}
                        textAnchor="middle"
                    >
                        {h < 24 ? `${h}:00` : ''}
                    </text>
                ))}

                {/* Grid lines */}
                {hours.map((h) => (
                    <line
                        key={`g-${h}`}
                        x1={LABEL_WIDTH + h * HOUR_WIDTH}
                        y1={HEADER_HEIGHT}
                        x2={LABEL_WIDTH + h * HOUR_WIDTH}
                        y2={totalHeight}
                        stroke="rgba(178,140,80,0.1)"
                        strokeWidth={1}
                    />
                ))}

                {/* Vehicle rows */}
                {vehicles.map((vehicle, i) => {
                    const y = HEADER_HEIGHT + i * ROW_HEIGHT;
                    const hasMissing = vehicle.depot_availability?.some(
                        (w) => w.available_from === 'MISSING'
                    );
                    const label = vehicle.route_name || vehicle.route_id || `Vehicle ${i + 1}`;

                    return (
                        <g
                            key={i}
                            onClick={() => hasMissing && onVehicleClick?.(i)}
                            style={{ cursor: hasMissing ? 'pointer' : 'default' }}
                        >
                            {/* Row background */}
                            <rect
                                x={0}
                                y={y}
                                width={LABEL_WIDTH + CHART_WIDTH}
                                height={ROW_HEIGHT}
                                fill={i % 2 === 0 ? 'rgba(178,140,80,0.03)' : 'transparent'}
                            />

                            {/* Route label */}
                            <text
                                x={8}
                                y={y + ROW_HEIGHT / 2 + 4}
                                fontSize={11}
                                fill={hasMissing ? '#ed6c02' : vortxColors.white}
                                fontFamily={vortxTypography.body}
                            >
                                {hasMissing ? '\u26A0 ' : ''}{label} ({vehicle.daily_miles}mi)
                            </text>

                            {/* Availability bars */}
                            {vehicle.depot_availability?.map((window, j) => {
                                const from = timeToMinutes(window.available_from);
                                const until = timeToMinutes(window.available_until);

                                if (window.available_from === 'MISSING') {
                                    // Amber bar spanning start of day to departure
                                    const endX = until != null ? (until / 1440) * CHART_WIDTH : CHART_WIDTH;
                                    return (
                                        <rect
                                            key={j}
                                            x={LABEL_WIDTH}
                                            y={y + 4}
                                            width={endX}
                                            height={ROW_HEIGHT - 8}
                                            rx={3}
                                            fill="rgba(237,108,2,0.3)"
                                            stroke="#ed6c02"
                                            strokeWidth={1}
                                            strokeDasharray="4,2"
                                        />
                                    );
                                }

                                if (from == null || until == null) return null;

                                const x = (from / 1440) * CHART_WIDTH;
                                const w = ((until - from) / 1440) * CHART_WIDTH;

                                return (
                                    <Tooltip
                                        key={j}
                                        title={`${window.available_from} — ${window.available_until}`}
                                    >
                                        <rect
                                            x={LABEL_WIDTH + x}
                                            y={y + 4}
                                            width={Math.max(w, 2)}
                                            height={ROW_HEIGHT - 8}
                                            rx={3}
                                            fill="rgba(178,140,80,0.4)"
                                            stroke={vortxColors.gold}
                                            strokeWidth={1}
                                        />
                                    </Tooltip>
                                );
                            })}
                        </g>
                    );
                })}
            </svg>
        </Box>
    );
}

export default FleetTimeline;
